import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  derivePurchasePayableReconciliationNumber,
  deriveSupplierCreditNumber,
  deriveSupplierPaymentNumber,
  deriveSupplierPaymentReversalNumber,
  deriveSupplierRefundNumber,
} from "./purchase-payable-number.js";
import { derivePurchaseReceiptNumber } from "./purchase-receipt-number.js";
import { derivePurchaseReturnNumber } from "./purchase-return-number.js";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const SupplierCodeSchema = Type.String({
  minLength: 1,
  maxLength: 40,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
});
const CanonicalTextSchema = (maximum: number) =>
  Type.String({ minLength: 1, maxLength: maximum, pattern: "^(?:\\S|\\S[\\s\\S]*\\S)$" });
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const TimestampSchema = Type.String({ minLength: 20, maxLength: 64 });
const AmountFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const SignedAmountFenSchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const SequenceSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const BalanceVersionSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const BusinessNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^(?:SH|TH|DZ|FK|DX|GT|CX)-[0-9]{8}-[0-9A-F]{12}$",
});

export const SupplierSettlementLedgerEntryKindSchema = Type.Union([
  Type.Literal("PURCHASE_RECEIPT"),
  Type.Literal("PURCHASE_RETURN"),
  Type.Literal("PAYABLE_RECONCILIATION"),
  Type.Literal("SUPPLIER_PAYMENT"),
  Type.Literal("SUPPLIER_BATCH_PAYMENT"),
  Type.Literal("SUPPLIER_CREDIT"),
  Type.Literal("SUPPLIER_REFUND"),
  Type.Literal("SUPPLIER_PAYMENT_REVERSAL"),
]);
export type SupplierSettlementLedgerEntryKind = Static<
  typeof SupplierSettlementLedgerEntryKindSchema
>;

export const SupplierSettlementLedgerEntrySchema = Type.Object(
  {
    kind: SupplierSettlementLedgerEntryKindSchema,
    businessId: StableIdSchema,
    businessNumber: BusinessNumberSchema,
    receiptId: Type.Union([StableIdSchema, Type.Null()]),
    receiptNumber: Type.Union([
      Type.String({ pattern: "^SH-[0-9]{8}-[0-9A-F]{12}$" }),
      Type.Null(),
    ]),
    purchaseOrderId: Type.Union([StableIdSchema, Type.Null()]),
    purchaseOrderNumber: Type.Union([
      Type.String({ pattern: "^CG-[0-9]{8}-[0-9A-F]{12}$" }),
      Type.Null(),
    ]),
    relatedReceiptCount: Type.Integer({ minimum: 1, maximum: 100 }),
    businessDate: BusinessDateSchema,
    businessAmountFen: AmountFenSchema,
    payableOutstandingFenBefore: AmountFenSchema,
    payableOutstandingFenAfter: AmountFenSchema,
    supplierRefundDueFenBefore: AmountFenSchema,
    supplierRefundDueFenAfter: AmountFenSchema,
    payableOutstandingDeltaFen: SignedAmountFenSchema,
    supplierRefundDueDeltaFen: SignedAmountFenSchema,
    payableBalanceVersionBefore: Type.Union([BalanceVersionSchema, Type.Null()]),
    payableBalanceVersionAfter: Type.Union([
      Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    gatewaySequence: SequenceSchema,
    occurredAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type SupplierSettlementLedgerEntry = Static<typeof SupplierSettlementLedgerEntrySchema>;

export const SupplierSettlementLedgerSummarySchema = Type.Object(
  {
    receiptCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    openPayableReceiptCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    supplierRefundDueReceiptCount: Type.Integer({
      minimum: 0,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    settledReceiptCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    payableOutstandingFen: AmountFenSchema,
    supplierRefundDueFen: AmountFenSchema,
  },
  { additionalProperties: false },
);
export type SupplierSettlementLedgerSummary = Static<typeof SupplierSettlementLedgerSummarySchema>;

export const SupplierSettlementLedgerQuerySchema = Type.Object(
  {
    beforeGatewaySequence: Type.Union([SequenceSchema, Type.Null()]),
    limit: Type.Integer({ minimum: 1, maximum: 100 }),
  },
  { additionalProperties: false },
);
export type SupplierSettlementLedgerQuery = Static<typeof SupplierSettlementLedgerQuerySchema>;

export const SupplierSettlementLedgerPageSchema = Type.Object(
  {
    supplierId: StableIdSchema,
    supplierCode: Type.Union([SupplierCodeSchema, Type.Null()]),
    supplierName: Type.Union([CanonicalTextSchema(80), Type.Null()]),
    supplierSnapshotSource: Type.Union([
      Type.Literal("LATEST_SETTLEMENT_FACT"),
      Type.Literal("NO_SETTLEMENT_FACT"),
    ]),
    summary: SupplierSettlementLedgerSummarySchema,
    asOfGatewaySequence: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    limit: Type.Integer({ minimum: 1, maximum: 100 }),
    entries: Type.Array(SupplierSettlementLedgerEntrySchema, { maxItems: 100 }),
    hasMore: Type.Boolean(),
    nextBeforeGatewaySequence: Type.Union([SequenceSchema, Type.Null()]),
    generatedAt: TimestampSchema,
    evidence: Type.Literal("GATEWAY_AUTHORITY"),
  },
  { additionalProperties: false },
);
export type SupplierSettlementLedgerPage = Static<typeof SupplierSettlementLedgerPageSchema>;

export const SupplierSettlementLedgerDirectoryEntrySchema = Type.Object(
  {
    supplierId: StableIdSchema,
    supplierCode: SupplierCodeSchema,
    supplierName: CanonicalTextSchema(80),
    enabled: Type.Union([Type.Boolean(), Type.Null()]),
    identitySource: Type.Union([
      Type.Literal("LOCAL_MASTER_SNAPSHOT"),
      Type.Literal("LATEST_SETTLEMENT_FACT"),
    ]),
    lastKnownAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type SupplierSettlementLedgerDirectoryEntry = Static<
  typeof SupplierSettlementLedgerDirectoryEntrySchema
>;

export const SupplierSettlementLedgerDirectorySchema = Type.Object(
  {
    suppliers: Type.Array(SupplierSettlementLedgerDirectoryEntrySchema, { maxItems: 10_000 }),
    generatedAt: TimestampSchema,
    evidence: Type.Literal("GATEWAY_LOCAL_DIRECTORY"),
  },
  { additionalProperties: false },
);
export type SupplierSettlementLedgerDirectory = Static<
  typeof SupplierSettlementLedgerDirectorySchema
>;

const BUSINESS_NUMBER_PREFIX: Readonly<Record<SupplierSettlementLedgerEntryKind, string>> = {
  PURCHASE_RECEIPT: "SH-",
  PURCHASE_RETURN: "TH-",
  PAYABLE_RECONCILIATION: "DZ-",
  SUPPLIER_PAYMENT: "FK-",
  SUPPLIER_BATCH_PAYMENT: "FK-",
  SUPPLIER_CREDIT: "DX-",
  SUPPLIER_REFUND: "GT-",
  SUPPLIER_PAYMENT_REVERSAL: "CX-",
};

function isExclusiveSupplierBalance(payableOutstandingFen: number, supplierRefundDueFen: number) {
  return payableOutstandingFen === 0 || supplierRefundDueFen === 0;
}

function expectedBusinessNumber(entry: SupplierSettlementLedgerEntry): string | null {
  try {
    switch (entry.kind) {
      case "PURCHASE_RECEIPT":
        return derivePurchaseReceiptNumber(entry.businessDate, entry.businessId);
      case "PURCHASE_RETURN":
        return derivePurchaseReturnNumber(entry.businessDate, entry.businessId);
      case "PAYABLE_RECONCILIATION":
        return derivePurchasePayableReconciliationNumber(entry.businessDate, entry.businessId);
      case "SUPPLIER_PAYMENT":
      case "SUPPLIER_BATCH_PAYMENT":
        return deriveSupplierPaymentNumber(entry.businessDate, entry.businessId);
      case "SUPPLIER_CREDIT":
        return deriveSupplierCreditNumber(entry.businessDate, entry.businessId);
      case "SUPPLIER_REFUND":
        return deriveSupplierRefundNumber(entry.businessDate, entry.businessId);
      case "SUPPLIER_PAYMENT_REVERSAL":
        return deriveSupplierPaymentReversalNumber(entry.businessDate, entry.businessId);
    }
  } catch {
    return null;
  }
}

function hasValidFinancialEffect(entry: SupplierSettlementLedgerEntry): boolean {
  if (entry.businessAmountFen <= 0) return false;
  const amount = BigInt(entry.businessAmountFen);
  const netBefore =
    BigInt(entry.payableOutstandingFenBefore) - BigInt(entry.supplierRefundDueFenBefore);
  const netAfter =
    BigInt(entry.payableOutstandingFenAfter) - BigInt(entry.supplierRefundDueFenAfter);
  switch (entry.kind) {
    case "PURCHASE_RECEIPT":
      return netAfter - netBefore === amount;
    case "PURCHASE_RETURN":
    case "SUPPLIER_PAYMENT":
    case "SUPPLIER_BATCH_PAYMENT":
    case "SUPPLIER_CREDIT":
      return netBefore - netAfter === amount;
    case "PAYABLE_RECONCILIATION":
      return (
        entry.supplierRefundDueFenBefore === 0 &&
        entry.supplierRefundDueFenAfter === 0 &&
        netAfter === amount
      );
    case "SUPPLIER_REFUND":
    case "SUPPLIER_PAYMENT_REVERSAL":
      return netAfter - netBefore === amount;
  }
}

export function isSupplierSettlementLedgerEntry(
  value: unknown,
): value is SupplierSettlementLedgerEntry {
  if (!Value.Check(SupplierSettlementLedgerEntrySchema, value)) return false;
  const entry = value as SupplierSettlementLedgerEntry;
  const isBatchPayment = entry.kind === "SUPPLIER_BATCH_PAYMENT";
  const hasSingleReceiptContext =
    entry.receiptId !== null &&
    entry.receiptNumber !== null &&
    entry.purchaseOrderId !== null &&
    entry.purchaseOrderNumber !== null &&
    entry.relatedReceiptCount === 1;
  const hasBatchReceiptContext =
    entry.receiptId === null &&
    entry.receiptNumber === null &&
    entry.purchaseOrderId === null &&
    entry.purchaseOrderNumber === null &&
    entry.relatedReceiptCount >= 2;
  const hasValidBalanceVersion = isBatchPayment
    ? entry.payableBalanceVersionBefore === null && entry.payableBalanceVersionAfter === null
    : entry.payableBalanceVersionBefore !== null &&
      entry.payableBalanceVersionAfter !== null &&
      entry.payableBalanceVersionAfter === entry.payableBalanceVersionBefore + 1;
  return (
    entry.businessNumber.startsWith(BUSINESS_NUMBER_PREFIX[entry.kind]) &&
    entry.businessNumber === expectedBusinessNumber(entry) &&
    (isBatchPayment ? hasBatchReceiptContext : hasSingleReceiptContext) &&
    isExclusiveSupplierBalance(
      entry.payableOutstandingFenBefore,
      entry.supplierRefundDueFenBefore,
    ) &&
    isExclusiveSupplierBalance(entry.payableOutstandingFenAfter, entry.supplierRefundDueFenAfter) &&
    entry.payableOutstandingDeltaFen ===
      entry.payableOutstandingFenAfter - entry.payableOutstandingFenBefore &&
    entry.supplierRefundDueDeltaFen ===
      entry.supplierRefundDueFenAfter - entry.supplierRefundDueFenBefore &&
    hasValidFinancialEffect(entry) &&
    hasValidBalanceVersion &&
    (entry.kind !== "PURCHASE_RECEIPT" ||
      (entry.payableBalanceVersionBefore === 0 &&
        entry.payableOutstandingFenBefore === 0 &&
        entry.supplierRefundDueFenBefore === 0))
  );
}

export function isSupplierSettlementLedgerDirectory(
  value: unknown,
): value is SupplierSettlementLedgerDirectory {
  if (!Value.Check(SupplierSettlementLedgerDirectorySchema, value)) return false;
  const directory = value as SupplierSettlementLedgerDirectory;
  const supplierIds = new Set<string>();
  const supplierCodes = new Set<string>();
  for (const supplier of directory.suppliers) {
    const normalizedCode = supplier.supplierCode.toLocaleLowerCase("en-US");
    if (
      supplierIds.has(supplier.supplierId) ||
      supplierCodes.has(normalizedCode) ||
      (supplier.identitySource === "LOCAL_MASTER_SNAPSHOT") !== (supplier.enabled !== null)
    ) {
      return false;
    }
    supplierIds.add(supplier.supplierId);
    supplierCodes.add(normalizedCode);
  }
  return true;
}

export function isSupplierSettlementLedgerPage(
  value: unknown,
): value is SupplierSettlementLedgerPage {
  if (!Value.Check(SupplierSettlementLedgerPageSchema, value)) return false;
  const page = value as SupplierSettlementLedgerPage;
  const hasSettlementSnapshot =
    page.supplierCode !== null &&
    page.supplierName !== null &&
    page.supplierSnapshotSource === "LATEST_SETTLEMENT_FACT";
  const hasNoSettlementSnapshot =
    page.supplierCode === null &&
    page.supplierName === null &&
    page.supplierSnapshotSource === "NO_SETTLEMENT_FACT";
  if (
    (page.summary.receiptCount === 0 ? !hasNoSettlementSnapshot : !hasSettlementSnapshot) ||
    page.summary.receiptCount !==
      page.summary.openPayableReceiptCount +
        page.summary.supplierRefundDueReceiptCount +
        page.summary.settledReceiptCount ||
    page.entries.length > page.limit ||
    !page.entries.every(isSupplierSettlementLedgerEntry) ||
    !page.entries.every(
      (entry, index) =>
        index === 0 || entry.gatewaySequence < (page.entries[index - 1]?.gatewaySequence ?? 0),
    ) ||
    page.entries.some((entry) => entry.gatewaySequence > page.asOfGatewaySequence)
  ) {
    return false;
  }
  const lastSequence = page.entries.at(-1)?.gatewaySequence ?? null;
  return page.hasMore
    ? lastSequence !== null && page.nextBeforeGatewaySequence === lastSequence
    : page.nextBeforeGatewaySequence === null;
}
